import React, { useState, useEffect, useCallback } from 'react';
import { useCluster } from '../context/ClusterContext';
import { useNavigate } from 'react-router-dom';
import PodLogsDrawer from '../components/PodLogsDrawer';
import WorkloadDetailDrawer from '../components/WorkloadDetailDrawer';
import Pager from '../components/Pager';
import useResourceWatch from '../hooks/useResourceWatch';
import { clustersAPI } from '../services/api';
import WebTerminal from '../components/WebTerminal';
import './ResourcePage.css';

// Map a pod status/phase to a distinct status-badge colour class so each
// state is visually identifiable at a glance (Suggestion: unique colour per
// status). Handles both K8s phases and common container waiting reasons.
export function podStatusClass(status) {
  const s = (status || '').toLowerCase();
  if (s === 'running')                               return 'status-running';
  if (s === 'pending')                               return 'status-pending';
  if (s === 'succeeded')                             return 'status-succeeded';
  if (s === 'completed')                             return 'status-completed';
  if (s.includes('containercreating') || s.includes('initializing') || s.includes('podinitializing'))
                                                     return 'status-creating';
  if (s.includes('terminating'))                     return 'status-terminating';
  if (s.includes('crashloop'))                       return 'status-crashloop';
  if (s.includes('imagepull') || s.includes('errimage') || s.includes('imagepullbackoff'))
                                                     return 'status-imagepull';
  if (s === 'failed' || s.includes('error') || s.includes('evicted') || s.includes('oomkilled'))
                                                     return 'status-failed';
  if (s === 'unknown' || !s)                         return 'status-unknown';
  return 'status-unknown';
}

function Pods() {
  // `resources` (the shared all-namespaces snapshot) is still used for the namespace and
  // status dropdowns — those must list every value in the cluster, not just what happens
  // to be on the current page. The TABLE itself comes from the paged endpoint.
  const { selectedCluster, resources, resourcesError, refreshResources } = useCluster();
  const navigate = useNavigate();
  const [nsFilter, setNsFilter]         = useState('all');
  const [nameFilter, setNameFilter]     = useState('');       // raw input, updates every keystroke
  const [nameQuery, setNameQuery]       = useState('');       // debounced — this is what's sent to the server
  const [statusFilter, setStatusFilter] = useState('all');
  const [logPod, setLogPod]             = useState(null);
  // Full live configuration for one pod (SARVKUBE-63) — resources, ports, env,
  // volumes/PVCs, events, YAML. A drawer so the table's page/filter survive.
  const [detail, setDetail]             = useState(null);
  const [showTerminal, setShowTerminal] = useState(false);

  // Server-side paging (Steve): one page costs the same regardless of how many pods the
  // cluster has, so a 1285-pod cluster renders as fast as a 20-pod one.
  const [page, setPage]         = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [pageData, setPageData] = useState({ items: [], total: 0 });
  const [pageLoading, setPageLoading] = useState(false);
  const [pageError, setPageError]     = useState(null);

  const clusterId = selectedCluster?.id;

  // Debounce the name search box — search across the FULL cluster (all 13 pages,
  // not just the 50 rows already loaded) happens server-side now (Steve's `~`
  // substring filter, see rancher_client.steve_params), so every keystroke would
  // otherwise fire its own request.
  useEffect(() => {
    const t = setTimeout(() => setNameQuery(nameFilter.trim()), 400);
    return () => clearTimeout(t);
  }, [nameFilter]);

  const loadPage = useCallback(async () => {
    if (!clusterId) return;
    setPageLoading(true);
    setPageError(null);
    try {
      const { data } = await clustersAPI.getPodsPaged(clusterId, {
        page,
        pageSize,
        // Push namespace AND name to the server so `total`/results reflect the
        // FULL collection, not just whatever's on the current page.
        namespace: nsFilter === 'all' ? undefined : nsFilter,
        name: nameQuery || undefined,
      });
      setPageData({ items: data.items || [], total: data.total ?? 0 });
    } catch (e) {
      // Paged listing is Rancher-only (Steve); a non-Rancher cluster 404s. Say so plainly
      // rather than showing an empty table.
      const code = e?.response?.status;
      const detail = e?.response?.data?.detail;
      // An agent-based cluster with no agent connected returns detail as an OBJECT
      // ({message, action_required, network_type} — resources.py), not a string. Rendering
      // an object directly as a JSX child crashed the whole page with React error #31
      // (confirmed live on kube.sarv.com/pods, cluster jpr-dc-beta-13) — same object shape
      // ClusterResources.js already unwraps correctly, this page just never got the fix.
      const msg = typeof detail === 'object' ? (detail?.message || 'Agent not connected') : detail;
      setPageError(
        code === 404
          ? 'Paged listing needs a managed cluster.'
          : (msg || 'Failed to load pods')
      );
      setPageData({ items: [], total: 0 });
    } finally {
      setPageLoading(false);
    }
  }, [clusterId, page, pageSize, nsFilter, nameQuery]);

  useEffect(() => { loadPage(); }, [loadPage]);

  // Changing the namespace, name search, or page size re-pages from the start;
  // staying on page 9 of a filter that now has 2 pages would show an empty table.
  useEffect(() => { setPage(1); }, [nsFilter, nameQuery, pageSize, clusterId]);

  // ── Live updates ───────────────────────────────────────────────────────────
  // Patch rows in place as the cluster changes, rather than re-polling. Events for pods
  // that aren't on this page are counted, not injected — inserting them would put them in
  // the wrong position in a server-sorted page — and the count offers a refresh.
  const [pendingNew, setPendingNew] = useState(0);

  const patchRow = useCallback((key, flat) => {
    setPageData(prev => {
      const i = prev.items.findIndex(p => `${p.namespace}/${p.name}` === key);
      if (i === -1) return prev;                 // not on this page
      const items = prev.items.slice();
      items[i] = { ...items[i], ...flat };
      return { ...prev, items };
    });
  }, []);

  const removeRow = useCallback((key) => {
    setPageData(prev => {
      const items = prev.items.filter(p => `${p.namespace}/${p.name}` !== key);
      if (items.length === prev.items.length) return prev;
      return { items, total: Math.max(0, prev.total - 1) };
    });
  }, []);

  const noteNew = useCallback((key, flat) => {
    // A create may be a pod we already show (Steve re-sends on reconnect) — patch those
    // instead of double-counting.
    setPageData(prev => {
      const exists = prev.items.some(p => `${p.namespace}/${p.name}` === key);
      if (exists) {
        const items = prev.items.map(p =>
          `${p.namespace}/${p.name}` === key ? { ...p, ...flat } : p);
        return { ...prev, items };
      }
      setPendingNew(n => n + 1);
      return prev;
    });
  }, []);

  const { connected: liveConnected } = useResourceWatch(clusterId, 'pods', {
    // Live watch is Rancher-only; don't open a socket that will be refused.
    enabled: !!clusterId && !pageError,
    namespace: nsFilter === 'all' ? undefined : nsFilter,
    onPatch: patchRow,
    onRemove: removeRow,
    onNew: noteNew,
  });

  // Any deliberate reload clears the "new pods appeared" nudge.
  useEffect(() => { setPendingNew(0); }, [page, pageSize, nsFilter, clusterId]);

  if (!selectedCluster) {
    return (
      <div className="resource-page">
        <div className="page-header"><h1>Pods</h1></div>
        <div className="no-cluster-msg">
          No cluster selected. <span className="link" onClick={() => navigate('/clusters')}>Add or select a cluster</span> to view pods.
        </div>
      </div>
    );
  }

  if (!selectedCluster.is_imported) {
    return (
      <div className="resource-page">
        <div className="page-header"><h1>Pods — {selectedCluster.name}</h1></div>
        <div className="no-cluster-msg">
          Live resource viewing is available for imported clusters only.<br />
          Use <code>kubectl get pods --all-namespaces</code> directly.
        </div>
      </div>
    );
  }

  // Dropdown options come from the cluster-wide snapshot so they list every namespace and
  // status that exists — not just the ones on the current page.
  const allPods = resources?.pods || [];
  const namespaces = ['all', ...new Set(allPods.map(p => p.namespace))].sort();
  const statuses   = ['all', ...new Set(allPods.map(p => p.status))].sort();

  // Namespace and name are filtered server-side (see loadPage) — name search covers
  // the FULL collection via Steve's substring filter, not just this page. Status is
  // still applied to the current page only: that remains a deliberate limit (Steve
  // has no generic "phase" field to filter on the way it does metadata.name/namespace).
  const rows = pageData.items.filter(p => statusFilter === 'all' || p.status === statusFilter);
  const localFilterActive = statusFilter !== 'all';

  return (
    <div className="resource-page">
      <div className="page-header">
        <div>
          <h1>Pods</h1>
          <div className="resource-cluster-badge">{selectedCluster.name}</div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-secondary" style={{ fontFamily: 'monospace' }}
            onClick={() => setShowTerminal(true)}>⌨ Terminal</button>
          <button className="btn btn-secondary" onClick={() => { loadPage(); refreshResources(); }}>Refresh</button>
        </div>
      </div>

      {(pageError || resourcesError) && <div className="resource-error">{pageError || resourcesError}</div>}

      {pageLoading && pageData.items.length === 0 ? (
        <div className="resource-loading">Loading pods...</div>
      ) : (
        <>
          <div className="resource-filter">
            <select value={nsFilter} onChange={e => setNsFilter(e.target.value)}>
              {namespaces.map(ns => (
                <option key={ns} value={ns}>{ns === 'all' ? 'All namespaces' : ns}</option>
              ))}
            </select>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              {statuses.map(s => (
                <option key={s} value={s}>{s === 'all' ? 'All statuses' : s}</option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Filter by name..."
              value={nameFilter}
              onChange={e => setNameFilter(e.target.value)}
            />
            <span className="resource-count">{localFilterActive ? `${rows.length} of ${pageData.items.length} on page (${pageData.total} total)` : `${pageData.total} pods`}</span>
            <span title={liveConnected ? 'Live — updates pushed from the cluster' : 'Not live — showing a fetched snapshot'}
                  style={{ fontSize: 12, color: liveConnected ? 'var(--color-success-500)' : 'var(--muted)' }}>
              {liveConnected ? '● live' : '○ offline'}
            </span>
            {pendingNew > 0 && (
              <button className="btn btn-secondary" style={{ padding: '2px 10px', fontSize: 12 }}
                onClick={() => { setPendingNew(0); loadPage(); }}>
                ↻ {pendingNew} new — refresh
              </button>
            )}
          </div>

          <div className="table-card">
            <table className="ds">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Namespace</th>
                  <th>Status</th>
                  <th>Ready</th>
                  <th>Restarts</th>
                  <th>Node</th>
                  <th>Age</th>
                  <th>Logs</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={8} style={{ textAlign: 'center', padding: 32, color: 'var(--muted)' }}>No pods match filter</td></tr>
                ) : rows.map(p => (
                  <tr key={`${p.namespace}/${p.name}`}>
                    <td><code style={{ fontSize: 12 }}>{p.name}</code></td>
                    <td style={{ color: 'var(--ink-2)', fontSize: 13 }}>{p.namespace}</td>
                    <td>
                      <span className={`status-badge ${podStatusClass(p.status)}`} title={p.status}>
                        {p.status}
                      </span>
                    </td>
                    <td>{p.ready}</td>
                    <td style={{ color: p.restarts > 0 ? 'var(--color-danger-500)' : 'var(--color-success-500)', fontWeight: p.restarts > 0 ? 600 : 400 }}>
                      {p.restarts}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--muted)' }}>{p.node}</td>
                    <td style={{ fontSize: 12, color: 'var(--muted)' }}>
                      {p.created_at ? new Date(p.created_at).toLocaleDateString() : 'N/A'}
                    </td>
                    <td style={{ display: 'flex', gap: 6 }}>
                      <button
                        className="btn btn-secondary"
                        style={{ padding: '3px 10px', fontSize: 12 }}
                        onClick={() => setDetail({ namespace: p.namespace, name: p.name })}
                        title="Resources, ports, environment, volumes, events, YAML"
                      >
                        Details
                      </button>
                      <button
                        className="btn btn-secondary"
                        style={{ padding: '3px 10px', fontSize: 12 }}
                        onClick={() => setLogPod({ namespace: p.namespace, name: p.name })}
                      >
                        📋 Logs
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pager
            page={page}
            pageSize={pageSize}
            total={pageData.total}
            loading={pageLoading}
            onPage={setPage}
            onPageSize={setPageSize}
          />

          {localFilterActive && (
            <div className="resource-hint" style={{ fontSize: 12, opacity: 0.75 }}>
              Status filter applies to this page only. Namespace and name search already
              cover the whole cluster.
            </div>
          )}
        </>
      )}

      {showTerminal && (
        <WebTerminal
          clusterId={selectedCluster.id}
          onClose={() => setShowTerminal(false)}
        />
      )}

      {logPod && (
        <PodLogsDrawer
          clusterId={selectedCluster.id}
          namespace={logPod.namespace}
          podName={logPod.name}
          onClose={() => setLogPod(null)}
        />
      )}

      {detail && selectedCluster && (
        <WorkloadDetailDrawer
          clusterId={selectedCluster.id}
          kind="Pod"
          namespace={detail.namespace}
          name={detail.name}
          onClose={() => setDetail(null)}
        />
      )}
    </div>
  );
}

export default Pods;
